import styled from '@emotion/styled';
import {
  SearchField,
  DateTextField,
  Spacing,
  StyledDiv,
  Typography,
  Divider,
  StyledFixedButtonWrapper,
  Button,
} from '@taleemabad/dsm/components';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';

interface Teacher {
  id: string;
  name: string;
}

export interface CoachScheduleVisitsProps {
  schoolName: string;
  emis: string;
  teachers: Teacher[];
  searchValue: string;
  onSearchChange: (value: string) => void;
  plannedDates: Record<string, string>;
  onPlannedDateChange: (teacherId: string, date: string) => void;
  plannedDateErrors?: Record<string, string>;
  onSave?: () => void;
  saving?: boolean;
  minDate?: string;
  maxDate?: string;
  completedTeacherIds?: string[];
}

export const CoachScheduleVisits = ({
  schoolName,
  emis,
  teachers,
  searchValue,
  onSearchChange,
  plannedDates,
  onPlannedDateChange,
  plannedDateErrors = {},
  onSave,
  saving = false,
  minDate,
  maxDate,
  completedTeacherIds = [],
}: CoachScheduleVisitsProps) => (
  <StyledDiv
    flexDirection="column"
    width="100%"
    padding="0 0 var(--rk-spacing-xxxxxl) 0"
  >
    <Typography variant="textTitleMedium">School</Typography>
    <Typography variant="textHeadlineMedium">EMIS {emis}</Typography>
    <Typography variant="textHeadlineMedium">{schoolName}</Typography>

    <Spacing marginBottom="xl" />

    <StyledTeacherSection>
      <Typography variant="textTitleMedium">Teacher</Typography>
      <Spacing marginBottom="lg" />
      <StyledSearchFieldWrapper>
        <SearchField
          variant="outlined"
          value={searchValue}
          onChange={onSearchChange}
          label="Search Teacher"
        />
      </StyledSearchFieldWrapper>

      <Spacing marginBottom="xxl" />

      <StyledCardsContainer>
        {teachers.map((teacher, index) => (
          <StyledDiv key={teacher.id} flexDirection="column" gap={4}>
            <Typography variant="textTitleMedium">Teacher</Typography>
            <Typography variant="textTitleMedium">{teacher.name}</Typography>
            <Spacing marginBottom="md" />
            <DateTextField
              label="Planned Date"
              placeholder="Planned Date"
              value={plannedDates[teacher.id] || ''}
              onChange={(date) => onPlannedDateChange(teacher.id, date)}
              min={minDate}
              width="100%"
              disabled={completedTeacherIds.includes(teacher.id)}
              clearable={true}
            />

            {completedTeacherIds.includes(teacher.id) && (
              <>
                <Spacing marginBottom="md" />
                <Typography variant="textBodySmallOnSurfaceVariant">
                  Observation completed - can not reschedule
                </Typography>
              </>
            )}
            {plannedDateErrors[teacher.id] && (
              <>
                <Spacing marginTop="lg" />

                <Typography variant="textBodySmallSchemesError">
                  {plannedDateErrors[teacher.id]}
                </Typography>
              </>
            )}
            <Spacing marginTop="lg" />
            {index < teachers.length - 1 && (
              <StyledMobileDivider>
                <Divider />
                <Spacing marginBottom="lg" />
              </StyledMobileDivider>
            )}
          </StyledDiv>
        ))}
      </StyledCardsContainer>
    </StyledTeacherSection>
    <StyledFixedButtonWrapper>
      <Button type="submit" onClick={onSave} disabled={saving}>
        {saving ? 'Saving...' : 'Save'}
      </Button>
    </StyledFixedButtonWrapper>
  </StyledDiv>
);

const StyledTeacherSection = styled.div`
  width: 100%;
`;

const StyledSearchFieldWrapper = styled.div`
  width: 100%;
  ${mq(BreakpointSizes.desktop)} {
    width: 50%;
  }
`;

const StyledCardsContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--rk-spacing-lg);

  ${mq(BreakpointSizes.desktop)} {
    grid-template-columns: repeat(3, 1fr);
    gap: 0 24px;
  }
`;

const StyledMobileDivider = styled.div`
  ${mq(BreakpointSizes.desktop)} {
    display: none;
  }
`;
